import React, { useEffect } from 'react';

import { Typography } from '@mui/material';
import { useNormalizeReferences } from 'hooks/useNormalizeReferences';
import { CommissionsNameInput } from 'pages/Commissions/components/CommissionsNameInput';
import { NewVersionCommission } from 'pages/Merchants/MerchantsShow/MerchantShowCommissions/MerchantShowCommissionsCreateNewVersionDialog/MerchantShowCommissionsCreateNewVersionDialog';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { RaRecord, required } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Box } from 'shared/mui/Box';
import {
  AutocompleteInput,
  BooleanInput,
  WatchedTextInput,
  AutocompleteMultiselectInput,
} from 'shared/react-admin/Inputs';
import { ReferenceArrayInput, ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { CommissionsCalculationMethodInput } from 'shared/widgets/CommissionsCalculationMethodInput/CommissionsCalculationMethodInput';
import { useCalculationMethodVisibility } from 'shared/widgets/CommissionsCalculationMethodInput/hooks/useCalculationMethodVisibility';
import { useResetCalculationMethodOnHide } from 'shared/widgets/CommissionsCalculationMethodInput/hooks/useResetCalculationMethodOnHide';

import { BoxAdditionalStyled, BoxMainStyled } from './MerchantShowCommissionsFormNewVersion.styled';
import { MerchantShowCommissionsFormNewVersionSkeleton } from './MerchantShowCommissionsFormNewVersionSkeleton';

type Props = {
  errors: string[];
  merchantId: string | undefined;
  record: NewVersionCommission | undefined;
};

export const MerchantShowCommissionsFormNewVersion: React.FC<Props> = ({
  errors,
  record,
  merchantId,
}) => {
  const { ready } = useNormalizeReferences({
    referenceFields: ['provider', 'merchant', 'currency', 'transactionTypes'],
    externalRecord: record,
  });

  const translate = useTypedTranslate();

  const { watch, trigger, setError } = useFormContext();
  const zeroed = watch('rate.zeroed');
  const isCalculationMethodVisible = useCalculationMethodVisibility();
  useResetCalculationMethodOnHide(isCalculationMethodVisible);
  const triggerPercentValidation = () =>
    trigger(['rate.percent', 'rate.fix', 'rate.max', 'rate.min']);

  useEffect(() => {
    errors.forEach((error: string) => {
      const [field, message] = error.split(':');
      setError(field, { type: 'custom', message });
    });
  }, [errors]);

  if (!ready) {
    return <MerchantShowCommissionsFormNewVersionSkeleton />;
  }

  return (
    <Box sx={{ padding: '24px 24px 0' }}>
      <Typography fontWeight={500} variant="body1">
        {translate('app.merchantCommissions.basicSettings')}
      </Typography>
      <BoxMainStyled>
        <CommissionsNameInput
          fullWidth
          label={translate('app.table.name')}
          size="small"
          source="name"
          sx={{ gridColumn: ' 1 / span 3' }}
          validate={required()}
          variant="outlined"
        />
        <ReferenceInput
          filter={{
            merchantAccounts: {
              merchants: [merchantId],
            },
          }}
          perPage={100}
          reference="providers"
          source="provider"
        >
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            fullWidth
            isRequired
            label={translate('app.table.provider')}
            optionText="name"
            readOnly={true}
            size="small"
            source="provider"
            variant="outlined"
          />
        </ReferenceInput>
        <ReferenceArrayInput
          perPage={100}
          reference="transaction_types/commission/list"
          source="transactionTypes"
        >
          <AutocompleteMultiselectInput
            isRequired
            label={translate('app.commissions.transactionTypes')}
            name="transactionTypes"
            optionText="name"
            readOnly={true}
            size="small"
            sx={{
              '.MuiTextField-root': {
                marginTop: 0,
              },
            }}
          />
        </ReferenceArrayInput>
        <ReferenceInput
          filter={{ active: true }}
          perPage={100}
          reference="currencies"
          sort={{ field: 'id', order: 'ASC' }}
          source="currency"
        >
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ currency: searchText })}
            fullWidth
            isRequired
            label={translate('app.table.currency')}
            optionText={(record: RaRecord) => `${record?.alphaCode}, ${record?.name}`}
            readOnly={true}
            size="small"
            variant="outlined"
          />
        </ReferenceInput>
      </BoxMainStyled>
      <Box alignItems="center" display="flex" justifyContent="space-between" width="100%">
        <Typography fontWeight={500} variant="body1">
          {translate('app.merchantCommissions.feeConditions')}
        </Typography>
        <BooleanInput
          defaultValue={false}
          label={translate('app.commissions.zeroed')}
          size="small"
          source="rate.zeroed"
          variant="outlined"
        />
      </Box>
      <BoxAdditionalStyled>
        <WatchedTextInput
          defaultValue={null}
          disabled={zeroed}
          fullWidth
          label={translate('app.commissions.percent')}
          onChange={triggerPercentValidation}
          size="small"
          source="rate.percent"
          variant="outlined"
          watchField="rate.zeroed"
        />
        <WatchedTextInput
          defaultValue={null}
          disabled={zeroed}
          fullWidth
          label={translate('app.commissions.fixed')}
          onChange={triggerPercentValidation}
          size="small"
          source="rate.fix"
          variant="outlined"
          watchField="rate.zeroed"
        />
        <WatchedTextInput
          defaultValue={null}
          disabled={zeroed}
          fullWidth
          label={translate('app.commissions.max')}
          onChange={triggerPercentValidation}
          size="small"
          source="rate.max"
          variant="outlined"
          watchField="rate.zeroed"
        />
        <WatchedTextInput
          defaultValue={null}
          disabled={zeroed}
          fullWidth
          label={translate('app.commissions.min')}
          onChange={triggerPercentValidation}
          size="small"
          source="rate.min"
          variant="outlined"
          watchField="rate.zeroed"
        />
      </BoxAdditionalStyled>
      {isCalculationMethodVisible && (
        <Box sx={{ mt: '10px' }}>
          <CommissionsCalculationMethodInput />
        </Box>
      )}
    </Box>
  );
};
